import { motionDurationsMs, motionEasings } from '@trellis/design'

/**
 * Motion Engine — shared token contract.
 *
 * Every Framer Motion spring, duration, and easing value used across the app
 * MUST originate here. Components should never hardcode stiffness/damping or
 * raw cubic-bezier arrays.
 *
 * CSS-level tokens live in globals.css (`--ease-drawer`, `--ease-out`, etc. —
 * canonical DESIGN.md names). This module is their Framer Motion counterpart.
 */

// ---------------------------------------------------------------------------
// Spring specs (Framer Motion `transition` objects)
// Codex SOTA values — researched from Linear/Vercel/Stripe/Raycast.
// ---------------------------------------------------------------------------

/** Page/route entry — the widest, most visible spring. */
export const pageEnterSpring = { type: 'spring' as const, visualDuration: 0.28, bounce: 0.08 }

/** Tab / section swap — faster than page enter, minimal overshoot. */
export const sectionSwapSpring = { type: 'spring' as const, visualDuration: 0.22, bounce: 0.06 }

/** Layout animations — tab indicators, shared elements. */
export const layoutSpring = { type: 'spring' as const, visualDuration: 0.2, bounce: 0.1 }

/** Hover spring — card lift, tooltip entrance. */
export const hoverSpring = { type: 'spring' as const, visualDuration: 0.18, bounce: 0.1 }

/**
 * Disclosure chevron rotation — collapsibles, expanders, section headers.
 * Critically damped: a rotating caret must never overshoot.
 */
export const disclosureSpring = { type: 'spring' as const, duration: 0.3, bounce: 0 }

/**
 * Interactive-state spring — hover, press, and toggle states the user can
 * REVERSE mid-flight (rapid hover in/out, quick press-release). Explicit
 * stiffness/damping so Motion preserves current velocity on interruption
 * instead of restarting from zero (DESIGN.md → Motion → Interruptibility).
 * Never a duration tween for these states.
 */
export const interactiveStateSpring = { type: 'spring' as const, stiffness: 520, damping: 38 }

/**
 * Floating overlay affordances entering over content — bottom bulk-action
 * bars, jump-to-latest pills, floating context panels. Snappy, no overshoot.
 */
export const floatingBarSpring = {
  type: 'spring' as const,
  stiffness: 380,
  damping: 30,
  mass: 0.6,
}

// Backward-compat aliases (used by 50+ files outside workflows)
export const interactiveSpring = hoverSpring
export const entrySpring = pageEnterSpring

// ---------------------------------------------------------------------------
// Easing curves (as [number, number, number, number] tuples for Framer)
// ---------------------------------------------------------------------------

/** `cubic-bezier(0.32, 0.72, 0, 1)` — CSS `--ease-drawer` (canonical DESIGN.md name). */
export const easePremium = motionEasings.drawer

/** Canonical alias for {@link easePremium}; matches the `--ease-drawer` CSS token. */
export const easeDrawer = easePremium

/** `cubic-bezier(0.16, 1, 0.3, 1)` — spring-like deceleration (CSS `--ease-spring`, legacy alias). */
export const easeSpring = motionEasings.spring

/** `cubic-bezier(0.23, 1, 0.32, 1)` — CSS `--ease-out` (canonical DESIGN.md name): entrances, exits, UI response. */
export const easeOut = motionEasings.out

// ---------------------------------------------------------------------------
// Duration constants (seconds — Framer convention)
// ---------------------------------------------------------------------------

/**
 * Durations in seconds (Framer convention).
 *
 * Shared millisecond values come from @trellis/design and are converted here
 * for Motion. CSS consumes the same generated values through adapter aliases.
 */
export const duration = {
  /** High-frequency micro-feedback: hover colour, press. Emil's band is 100-160ms. */
  instant: motionDurationsMs.instant / 1000,
  fast: motionDurationsMs.fast / 1000,
  normal: motionDurationsMs.normal / 1000,
  slow: motionDurationsMs.slow / 1000,
  /** Modals/drawers only — exempt from the 300ms UI cap. Do NOT use on inline elements. */
  slower: motionDurationsMs.entrance / 1000,
  entrance: motionDurationsMs.entrance / 1000,
} as const

// ---------------------------------------------------------------------------
// Tween presets (for fades that don't need spring oscillation)
// ---------------------------------------------------------------------------

// Derived from the duration scale so they can never drift from it.
export const fadeFast = { duration: duration.fast, ease: easeDrawer }
export const fadeNormal = { duration: duration.normal, ease: easeDrawer }

// ---------------------------------------------------------------------------
// Stagger presets
// ---------------------------------------------------------------------------

export const stagger = {
  /** Default list stagger — visible "pouring in" effect. */
  default: { staggerChildren: 0.028, delayChildren: 0.02 },
  /** Tighter stagger for dense grids. */
  tight: { staggerChildren: 0.018, delayChildren: 0 },
  /** Dramatic stagger for hero/showcase moments. */
  dramatic: { staggerChildren: 0.05, delayChildren: 0.04 },
} as const

// ---------------------------------------------------------------------------
// Reduced-motion overrides
// ---------------------------------------------------------------------------

/**
 * Apply to any Framer `transition` when the user prefers reduced motion.
 * Removes spring oscillation and collapses duration to near-instant.
 */
export const reducedMotionTransition = {
  type: 'tween' as const,
  duration: 0.01,
  ease: 'easeOut' as const,
} as const

// ---------------------------------------------------------------------------
// Hover & press presets
// ---------------------------------------------------------------------------

export const hoverLift = { y: -2 } as const
// Press-scale for substantial action buttons (Send, Save, Submit, primary CTA).
// 0.97 chosen per Emil Kowalski's design-eng framework — 0.985 was imperceptible.
// DO NOT use this token on chrome (sidebar, nav, tabs, list rows). Those should
// have zero press feedback to avoid motion fatigue from high-frequency clicks.
export const pressScale = { scale: 0.97 } as const
